import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { useSession } from '../auth/session.tsx';
import type { NewHomeInput } from './places.ts';

/**
 * Adding a client.
 *
 * The two kinds of client are one screen and two shapes, mirroring the
 * discriminated union the endpoint parses: a client the agency has not been
 * appointed for gets an X reference and Pipeline, and an appointed one gets a
 * numbered reference and a status derived from whether the order is temporary.
 * Neither the reference nor the status is ever sent — the server allocates one
 * and derives the other, so there is no way for this form to disagree with the
 * database about either.
 */

export type AppointingOrderType =
  | 'ORDER_AND_JUDGMENT_APPOINTING'
  | 'TEMPORARY_GUARDIAN_APPOINTED'
  | 'SHORT_FORM_ORDER'
  | 'SUCCESSOR_GUARDIAN_APPOINTED'
  | 'CO_GUARDIAN_APPOINTED'
  | 'AMENDED_ORDER';

/** What the person choosing sees, against what is stored. */
export const ORDER_TYPE_LABELS: Record<AppointingOrderType, string> = {
  ORDER_AND_JUDGMENT_APPOINTING: 'Order and Judgment appointing a guardian',
  TEMPORARY_GUARDIAN_APPOINTED: 'Order appointing a temporary guardian',
  SHORT_FORM_ORDER: 'Short Form Order',
  SUCCESSOR_GUARDIAN_APPOINTED: 'Order appointing a successor guardian',
  CO_GUARDIAN_APPOINTED: 'Order appointing a co-guardian',
  AMENDED_ORDER: 'Amended order',
};

/**
 * Co-guardianships are refused by the endpoint rather than recorded as SOLE,
 * because an overstated authority on the day a client is created is worse than
 * a second step. The form says so instead of offering it and being refused.
 */
export const INTAKE_CANNOT_RECORD: AppointingOrderType[] = [
  'CO_GUARDIAN_APPOINTED',
];

/**
 * Where they live, if known: a building, or a home recorded now. Exactly one.
 * No kind of placement: the server derives it from the building (061).
 */
export interface PlacementInput {
  facility_id?: string;
  home?: NewHomeInput;
  started_on?: string;
}

interface CommonInput {
  last_name: string;
  first_name?: string | null;
  middle_name?: string | null;
  suffix?: string | null;
  date_of_birth?: string;
  client_category?: string;
  primary_language?: string | null;
  communication_needs?: string | null;
  notes?: string | null;
  /** Required. Without it the client reads as years in arrears, not as new. */
  radar_date: string;
  radar_date_source?: string | null;
  radar_date_estimated?: boolean;
  placement?: PlacementInput;
}

export interface NewPipelineClient extends CommonInput {
  appointed: false;
}

export interface NewAppointedClient extends CommonInput {
  appointed: true;
  county_code: string;
  order: {
    order_type_code: AppointingOrderType;
    signed_date: string;
    grants_person: boolean;
    grants_property: boolean;
    is_temporary: boolean;
    court_name?: string | null;
    court_index_number?: string | null;
    commissioned_date?: string;
  };
  order_is_commission?: boolean;
  filing_method?: 'IN_PERSON' | 'ONLINE';
}

export type NewClientInput = NewPipelineClient | NewAppointedClient;

export interface CreatedClient {
  client: {
    id: string;
    client_ref: string;
    last_name: string;
    first_name: string | null;
    display_name: string;
    legal_status: string;
    client_category: string | null;
    whereabouts_unknown: boolean;
  };
  appointment_id: string | null;
  court_order_id: string | null;
  legal_status: string | null;
}

/**
 * On success the client lists are invalidated rather than patched: creating a
 * client also creates an appointment, an order, deadlines and possibly a
 * placement, and the server knows things about those consequences that this
 * mutation does not.
 */
export function useCreateClient() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: NewClientInput) =>
      apiFetch<CreatedClient>('/clients', getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['work'] });
    },
  });
}
